import React, { useEffect, useState } from 'react';
import { useResumeContext } from '../../context/ResumeContext';
import ExpandableSection from '../shared/ExpandableSection';
import SkillMeter from '../shared/SkillMeter';
import { careerData } from '../../data/careerData';
import { careerSkillHighlights } from '../../data/skillCategoryData';

/**
 * Career Journey Module Component
 * Shows the progression of career experiences as a learning journey
 */
const CareerJourneyModule = () => {
  const { markSectionViewed } = useResumeContext();
  const [showSkills, setShowSkills] = useState({});
  
  useEffect(() => {
    markSectionViewed('career');
  }, [markSectionViewed]);

  // Toggle showing skills for a specific career phase
  const toggleSkills = (careerId) => {
    setShowSkills(prev => ({
      ...prev,
      [careerId]: !prev[careerId]
    }));
  };

  // Get color theme based on learning phase
  const getPhaseTheme = (phase) => {
    if (phase.includes('Advanced')) return {
      bg: 'bg-green-100',
      text: 'text-green-800',
      border: 'border-green-500',
      accent: 'text-green-600',
      arrow: 'text-green-500'
    };
    if (phase.includes('Technical')) return {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-500',
      accent: 'text-blue-600',
      arrow: 'text-blue-500'
    };
    return {
      bg: 'bg-purple-100',
      text: 'text-purple-800',
      border: 'border-purple-500',
      accent: 'text-purple-600',
      arrow: 'text-purple-500'
    };
  };

  return (
    <div className="space-y-6">
      <section className="card border-l-4 border-green-500">
        <h2 className="text-xl font-bold text-green-700 mb-4 flex items-center gap-2">
          🚀 Professional Learning Journey
        </h2>
        <p className="mb-6">
          My career represents a continuous evolution spanning over 15 years, from early roles in visual 
          communication and sales to specialized technical documentation and education. This journey 
          reflects the development of complementary skills across diverse industries—retail management, 
          education, community organization, and print production—all contributing to my expertise in 
          transforming complex technical information into accessible learning experiences. Each role has 
          built upon previous experiences, creating a foundation for increasingly specialized technical 
          education work. Explore each phase to see how this professional narrative has unfolded.
        </p>
      </section>

      {/* Timeline visualization */}
      <div className="relative pb-12">
        <div className="absolute left-4 top-0 bottom-0 w-1 bg-slate-200 z-0"></div>
        
        {careerData.map((career, index) => {
          const theme = getPhaseTheme(career.learningPhase);
          const isLast = index === careerData.length - 1;
          
          return (
            <div key={career.id} className="relative z-10">
              {/* Timeline node */}
              <div className="absolute left-4 -ml-3.5 mt-6 h-7 w-7 rounded-full border-2 border-white bg-white shadow">
                <div className={`h-5 w-5 rounded-full ${theme.bg}`}></div>
              </div>
              
              {/* Connection line to next node */}
              {!isLast && (
                <div className="absolute left-4 top-12 bottom-0 w-1 bg-slate-200 z-0"></div>
              )}
              
              {/* Content card */}
              <div className="ml-12 mb-10">
                <ExpandableSection 
                  id={career.id} 
                  title={`${career.learningPhase} (${career.period.split(' (')[0]})`}
                >
                  <div>
                    <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
                      <span className={`inline-block px-2 py-1 ${theme.bg} ${theme.text} text-xs font-medium rounded`}>
                        {career.title}
                      </span>
                      <span className="text-slate-600">{career.company}</span>
                    </div>
                    <div className="text-slate-600">{career.period} | {career.location}</div>
                    
                    <p className="my-4 italic text-slate-600">{career.description}</p>
                    
                    {/* Skill category highlights */}
                    {careerSkillHighlights[career.id] && (
                      <div className="mb-4">
                        <h5 className="text-sm font-medium text-slate-700 mb-2">Key Skill Categories:</h5>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {careerSkillHighlights[career.id].map((highlight, idx) => (
                            <div 
                              key={idx} 
                              className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 flex items-center gap-1"
                            >
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: highlight.category.includes('Visual') ? '#FF9B42' : 
                                highlight.category.includes('Technical Documentation') ? '#00A9E0' : 
                                highlight.category.includes('Instructional') ? '#FF3E8A' :
                                highlight.category.includes('Technical Systems') ? '#57CC99' :
                                highlight.category.includes('Process') ? '#8884d8' : '#2D3142' }}></span>
                              <span>{highlight.category}</span>
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                          {careerSkillHighlights[career.id].map((highlight, idx) => (
                            <div 
                              key={`skills-${idx}`} 
                              className="text-sm border rounded p-2"
                              style={{ borderLeftWidth: '3px', borderLeftColor: highlight.category.includes('Visual') ? '#FF9B42' : 
                                highlight.category.includes('Technical Documentation') ? '#00A9E0' : 
                                highlight.category.includes('Instructional') ? '#FF3E8A' :
                                highlight.category.includes('Technical Systems') ? '#57CC99' :
                                highlight.category.includes('Process') ? '#8884d8' : '#2D3142' }}
                            >
                              <div className="font-medium mb-1" style={{ color: highlight.category.includes('Visual') ? '#FF9B42' : 
                                highlight.category.includes('Technical Documentation') ? '#00A9E0' : 
                                highlight.category.includes('Instructional') ? '#FF3E8A' :
                                highlight.category.includes('Technical Systems') ? '#57CC99' :
                                highlight.category.includes('Process') ? '#8884d8' : '#2D3142' }}>
                                {highlight.category.split(' & ')[0]}
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {highlight.skills.map((skill, skillIdx) => (
                                  <span 
                                    key={skillIdx}
                                    className="text-xs px-1.5 py-0.5 bg-slate-50 rounded border border-slate-200"
                                  >
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className={`p-4 rounded-lg mb-4 ${theme.bg}`}>
                      <h4 className={`font-bold ${theme.text} mb-2 flex items-center gap-2`}>
                        Learning Outcomes Acquired
                      </h4>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {career.learningOutcomes.map((outcome, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <span className={theme.accent}>✓</span>
                            <span>{outcome}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Skills section with toggle */}
                    <div className="mb-4">
                      <button 
                        onClick={() => toggleSkills(career.id)}
                        className={`flex items-center gap-2 font-bold ${theme.text} mb-2 p-2 rounded hover:bg-gray-100`}
                      >
                        <span>{showSkills[career.id] ? '▼' : '▶'}</span>
                        <span>Skills Developed</span>
                      </button>
                      
                      {showSkills[career.id] && (
                        <div className="space-y-3 p-2">
                          {career.skills.map((skill) => (
                            <SkillMeter
                              key={`${career.id}-${skill.name}`}
                              name={skill.name}
                              level={skill.level}
                              description={skill.description}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <h4 className="font-bold mb-3">Key Achievements</h4>
                    <div className="space-y-4">
                      {career.achievements.map((achievement, i) => (
                        <div key={i} className="border rounded-lg">
                          <h5 className="font-bold p-3 bg-slate-50 border-b">{achievement.title}</h5>
                          <ul className="p-3 space-y-2">
                            {achievement.details.map((detail, j) => (
                              <li key={j} className="flex items-start gap-2">
                                <span className={theme.arrow}>→</span>
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </ExpandableSection>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CareerJourneyModule;